import React, { useEffect, useRef, useCallback } from 'react';
import {
  View,
  Pressable,
  StyleSheet,
  Platform,
  Animated,
  Text,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import * as Haptics from 'expo-haptics';

import { useMenu } from '../../navigation/MenuContext';
import { RootState } from '../../store';
import { ActiveChargeAttributes } from '../../models/active-charges-model';

// Define root screens where hamburger should be shown
// These are screens directly accessible from the menu
const ROOT_SCREENS = new Set([
  'ChargerSelection',
  'TransactionHistory',
  'MyChargers',
  'HostInsights',
  'PartnerPortal',
  'PartnerEarningsDashboard',
  'AdminScreen',
  'OnboardingStatus',
  'AccountInfo',
  'PayoutRules',
  'ChargerStation', // Map view is considered root
]);

// Screens where the header should not be shown at all
const HIDDEN_HEADER_SCREENS = new Set([
  'WelcomeScreen',
  'LoginWithAccount',
  'CreateAccount',
  'resetPassword',
]);

interface HeaderWithMenuProps {
  title?: string;
  forceBackButton?: boolean;
  forceHamburger?: boolean;
  transparent?: boolean;
  onBackPress?: () => void;
  /** Set to true when HeaderWithMenu is inside a SafeAreaView to avoid double padding */
  insideSafeArea?: boolean;
  /** Set to true to use dark icon color (for light backgrounds) */
  darkIcon?: boolean;
}

const HeaderWithMenu: React.FC<HeaderWithMenuProps> = ({
  title,
  forceBackButton = false,
  forceHamburger = false,
  transparent = false,
  onBackPress,
  insideSafeArea = true, // Default to true since most screens use SafeAreaView
  darkIcon = false,
}) => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { openMenu } = useMenu();
  
  // Get current route name from navigation state
  const currentRouteName = useNavigationState((state) => {
    if (!state?.routes?.length) return undefined;
    return state.routes[state.index]?.name;
  });

  // Get navigation stack depth
  const canGoBack = useNavigationState((state) => {
    if (!state?.routes?.length) return false;
    return state.index > 0;
  });

  // Check for active charges
  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );
  const hasActiveCharge = activeCharges && activeCharges.length > 0;

  // Animation for icon press
  const scaleAnim = useRef(new Animated.Value(1)).current;

  // Determine if we should show hamburger or back arrow
  const isRootScreen = currentRouteName ? ROOT_SCREENS.has(currentRouteName) : true;
  const shouldShowHamburger = forceHamburger || (!forceBackButton && (isRootScreen || !canGoBack));

  // Animate scale on icon change
  useEffect(() => {
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web', // Native driver not supported on web
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: Platform.OS !== 'web', // Native driver not supported on web
      }),
    ]).start();
  }, [shouldShowHamburger]);

  // Handle button press
  const handlePress = useCallback(() => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }

    if (shouldShowHamburger) {
      openMenu();
    } else if (onBackPress) {
      onBackPress();
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [shouldShowHamburger, openMenu, onBackPress, navigation]);

  // Hide header on certain screens
  if (currentRouteName && HIDDEN_HEADER_SCREENS.has(currentRouteName)) {
    return null;
  }

  // Inline mode - render just the button (no extra container when no title)
  // This allows parent components to position it flexibly
  if (!title) {
    return (
      <Pressable
        onPress={handlePress}
        style={({ pressed }) => [
          styles.iconButton,
          { opacity: pressed ? 0.6 : 1 },
        ]}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <Animated.View
          style={[
            styles.iconContainer,
            {
              transform: [{ scale: scaleAnim }],
            },
          ]}
        >
          {shouldShowHamburger ? (
            <HamburgerIcon color={darkIcon ? '#1A1D21' : '#FFFFFF'} />
          ) : (
            <MaterialIcons name="arrow-back" size={24} color={darkIcon ? '#1A1D21' : '#FFFFFF'} />
          )}
        </Animated.View>

        {/* Active Charge Badge */}
        {shouldShowHamburger && hasActiveCharge && (
          <View style={styles.badge}>
            <View style={styles.badgeDot} />
          </View>
        )}
      </Pressable>
    );
  }

  // With title - render as a header row
  const topPadding = insideSafeArea ? 4 : insets.top + 4;
  
  return (
    <View
      style={[
        styles.container,
        {
          paddingTop: topPadding,
          backgroundColor: transparent ? 'transparent' : '#1A1D21',
        },
      ]}
    >
      <Pressable
        onPress={handlePress}
        style={({ pressed }) => [
          styles.iconButton,
          { opacity: pressed ? 0.6 : 1 },
        ]}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <Animated.View
          style={[
            styles.iconContainer,
            {
              transform: [
                { scale: scaleAnim },
              ],
            },
          ]}
        >
          {shouldShowHamburger ? (
            <HamburgerIcon color={darkIcon ? '#1A1D21' : '#FFFFFF'} />
          ) : (
            <MaterialIcons name="arrow-back" size={24} color={darkIcon ? '#1A1D21' : '#FFFFFF'} />
          )}
        </Animated.View>

        {/* Active Charge Badge */}
        {shouldShowHamburger && hasActiveCharge && (
          <View style={styles.badge}>
            <View style={styles.badgeDot} />
          </View>
        )}
      </Pressable>

      {/* Title - always show if provided */}
      {title && (
        <Text style={styles.title} numberOfLines={1}>
          {title}
        </Text>
      )}

      {/* Spacer for symmetry when title is shown */}
      {title && <View style={styles.spacer} />}
    </View>
  );
};

// Custom hamburger icon component - 3 equal horizontal lines
const HamburgerIcon: React.FC<{ color?: string }> = ({ color = '#FFFFFF' }) => (
  <View style={styles.hamburger}>
    <View style={[styles.hamburgerLine, { backgroundColor: color }]} />
    <View style={[styles.hamburgerLine, { backgroundColor: color }]} />
    <View style={[styles.hamburgerLine, { backgroundColor: color }]} />
  </View>
);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingBottom: 4,
    zIndex: 100,
  },
  iconButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 22,
  },
  iconContainer: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hamburger: {
    width: 22,
    height: 16,
    justifyContent: 'space-between',
  },
  hamburgerLine: {
    width: 22,
    height: 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 1,
  },
  badge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#1A1D21',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#39BEB7',
  },
  title: {
    flex: 1,
    fontSize: 17,
    fontFamily: 'PlusJakartaSans-SemiBold',
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  spacer: {
    width: 44, // Same width as iconButton to balance the header
    height: 44,
  },
});

export default HeaderWithMenu;

